import { put, call, takeLatest, takeEvery, cancelled } from 'redux-saga/effects';
import {
  CASE_FETCH_SEARCH_HISTORY_ITEM_BEGIN,
  CASE_FETCH_SEARCH_HISTORY_ITEM_SUCCESS,
  CASE_FETCH_SEARCH_HISTORY_ITEM_FAILURE,
  CASE_FETCH_SEARCH_HISTORY_ITEM_DISMISS_FEEDBACK,
  CASE_CANCEL_SEARCH_HISTORY_ITEM,
  CASE_CLEAR_SEARCH_HISTORY_ITEM,
} from './constants';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';

export const fetchSearchHistoryItem = createPromiseAction(CASE_FETCH_SEARCH_HISTORY_ITEM_BEGIN);

export const cancelFetchSearchHistoryItem = () => ({
  type: CASE_CANCEL_SEARCH_HISTORY_ITEM,
});

export const clearSearchHistoryItem = () => ({
  type: CASE_CLEAR_SEARCH_HISTORY_ITEM,
});

// Store for tracking cancellation state and AbortController
let isCancellationRequested = false;
let currentAbortController = null;

export function dismissFetchSearchHistoryItemFeedback() {
  return {
    type: CASE_FETCH_SEARCH_HISTORY_ITEM_DISMISS_FEEDBACK,
  };
}

// worker Saga: will be fired on CASE_FETCH_SEARCH_HISTORY_ITEM_BEGIN actions
export function* doFetchSearchHistoryItem(action) {
  const {
    payload: { caseId, searchHistoryItmId, omitFiles },
  } = action;

  // Cancel any existing request
  if (currentAbortController) {
    currentAbortController.abort();
  }

  // Reset cancellation flag and create new AbortController
  isCancellationRequested = false;
  currentAbortController = new AbortController();

  try {
    // Build the URL with optional query parameters
    let url = `/cases/${caseId}/search-history/${searchHistoryItmId}`;
    if (omitFiles !== undefined) {
      url += `?omitFiles=${omitFiles}`;
    }

    const res = yield call(api.get, url, { signal: currentAbortController.signal });

    // Check if cancellation was requested while the call was in progress
    if (isCancellationRequested) {
      // Request was cancelled after it completed - resolve as cancelled
      currentAbortController = null;
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Clear the controller on successful completion
    currentAbortController = null;

    if (res && res.error) {
      yield put({
        type: CASE_FETCH_SEARCH_HISTORY_ITEM_FAILURE,
        feedback: {
          message: 'feedback.fetchSearchHistoryItemFailure',
          error: res.error,
        },
      });
      return yield call(rejectPromiseAction, action, res.error);
    }
    // Dispatch success action
    yield put({
      type: CASE_FETCH_SEARCH_HISTORY_ITEM_SUCCESS,
      data: {
        results: res,
      },
    });
    yield call(resolvePromiseAction, action, res);
  } catch (error) {
    // Clear the controller on error
    currentAbortController = null;

    // Check if it was an abort error (cancellation)
    if (
      error.name === 'AbortError' ||
      (error.message &&
        (error.message.includes('aborted') || error.message.includes('signal is aborted'))) ||
      isCancellationRequested
    ) {
      // Request was cancelled - resolve as cancelled to prevent promise rejection
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Handle other errors
    yield put({
      type: CASE_FETCH_SEARCH_HISTORY_ITEM_FAILURE,
      feedback: {
        message: 'feedback.fetchSearchHistoryItemFailure',
        error: error.message || error,
      },
    });
    yield call(rejectPromiseAction, action, error);
  } finally {
    // Handle saga cancellation
    if (yield cancelled()) {
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      isCancellationRequested = true;
    }
  }
}

// worker Saga: will be fired on CASE_CANCEL_SEARCH_HISTORY_ITEM actions
export function doCancelFetchSearchHistoryItem() {
  // Set the cancellation flag
  isCancellationRequested = true;

  // Abort the current HTTP request
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchFetchSearchHistoryItem() {
  yield takeLatest(fetchSearchHistoryItem, withCurrentCaseId(doFetchSearchHistoryItem));
}

export function* watchCancelFetchSearchHistoryItem() {
  yield takeEvery(CASE_CANCEL_SEARCH_HISTORY_ITEM, doCancelFetchSearchHistoryItem);
}

export function useFetchSearchHistoryItem() {
  const dispatch = useDispatch();

  const { searchHistoryItem, fetchSearchHistoryItemPending, fetchSearchHistoryItemFeedback } =
    useSelector(
      (state) => ({
        searchHistoryItem: state.case.searchHistoryItem,
        fetchSearchHistoryItemPending: state.case.fetchSearchHistoryItemPending,
        fetchSearchHistoryItemFeedback: state.case.fetchSearchHistoryItemFeedback,
      }),
      shallowEqual,
    );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(fetchSearchHistoryItem(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissFetchSearchHistoryItemFeedback());
  }, [dispatch]);

  const boundCancelAction = useCallback(() => {
    return dispatch(cancelFetchSearchHistoryItem());
  }, [dispatch]);

  const boundClearAction = useCallback(() => {
    return dispatch(clearSearchHistoryItem());
  }, [dispatch]);

  return {
    searchHistoryItem,
    fetchSearchHistoryItem: boundAction,
    fetchSearchHistoryItemPending,
    fetchSearchHistoryItemFeedback,
    dismissFetchSearchHistoryItemFeedback: boundDismissFeedback,
    cancelFetchSearchHistoryItem: boundCancelAction,
    clearSearchHistoryItem: boundClearAction,
  };
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case CASE_FETCH_SEARCH_HISTORY_ITEM_BEGIN + '.TRIGGER':
      return {
        ...state,
        fetchSearchHistoryItemPending: true,
        fetchSearchHistoryItemFeedback: action.feedback,
        searchHistoryItem: [],
      };

    case CASE_FETCH_SEARCH_HISTORY_ITEM_SUCCESS:
      return {
        ...state,
        fetchSearchHistoryItemPending: false,
        fetchSearchHistoryItemFeedback: action.feedback,
        searchHistoryItem: action.data.results,
      };

    case CASE_FETCH_SEARCH_HISTORY_ITEM_FAILURE:
      return {
        ...state,
        fetchSearchHistoryItemPending: false,
        searchHistoryItem: [],
        fetchSearchHistoryItemFeedback: action.feedback,
      };

    case CASE_FETCH_SEARCH_HISTORY_ITEM_DISMISS_FEEDBACK:
      return {
        ...state,
        fetchSearchHistoryItemFeedback: null,
      };

    case CASE_CANCEL_SEARCH_HISTORY_ITEM:
      return {
        ...state,
        fetchSearchHistoryItemPending: false,
        fetchSearchHistoryItemFeedback: null,
      };

    case CASE_CLEAR_SEARCH_HISTORY_ITEM:
      return {
        ...state,
        searchHistoryItem: [],
      };

    default:
      return state;
  }
}
